/**
 * Checkouts e vendas (spec 003, 4.2): o painel fala direto com o servico de pagamentos, com o token do operador
 * (conferido la pelo JWKS do backend). Criar e desativar checkout e so ADMIN; ver pedidos e vendas, quem opera.
 * Nenhuma regra aqui: parcelas, juros e totais vem calculados do servico.
 */
import { cotacaoPorExtenso, cotacaoValida, realDoDolar } from "@mtc/contratos";
import { Abas, Aviso, Botao, BotaoCopiar, CabecalhoDePagina, Campo, CampoCaixa, CampoCpf, CampoData, CampoDinheiro, CampoSelecao, CampoTextoLongo, Carregando, Dialogo, ErroApi, Fatos, Motivo, Pilha, POR_PAGINA, Secao, Segmentado, Selo, SeloSemAssinatura, Tabela, TextoCopiavel, Vazio, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, type Tom } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { erroDe, useApi, usePapel } from "../api";
import { salvarArquivo } from "../baixar";
import { config } from "../config";
import { temPlataformaProfit, useAmbiente } from "../contexto";
import { emLista, palavrasDoPlanoQueFaltam } from "../nomeDoCheckout";
import { campoDeVoltarParaOk, voltarParaDoCampo } from "../voltarPara";
import { CredenciaisDoSite } from "./ApiDoSite";
import { DialogoBeneficiosDoCheckout, EscolhaDeBeneficios } from "./BeneficiosDaOferta";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { AbaGuru } from "./Guru";
import { usePlanos } from "./Matriculas";
import { CampoVoltarPara, DialogoVoltarPara } from "./VoltarPara";
import { usePagina } from "../pagina";

/** Endereco no servico de pagamentos do ambiente (o painel fala direto com ele, com o mesmo token). */
export const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;

type Checkout = { id: string; token: string; link: string | null; plano_slug: string | null; sem_assinatura: boolean; nome: string; plataforma: "PROFIT_ONE" | "PROFIT_PRO" | null; preco_centavos: number; anunciado_em_dolar: { preco_usd_centavos: number; cotacao: string; cotacao_em: string | null } | null; adicional_pro_centavos: number | null; preco_pro_centavos: number | null; meios: ("pix" | "cartao")[]; max_parcelas: number; juros_bps_por_parcela: number; termos: { url: string; versao: string } | null; /** Back redirect (spec 045, 3.10). Nulo: o site da marca. */ voltar_para: string | null; ativo: boolean; /** Aparece nos planos a venda da area do cliente (15/10). */ na_area: boolean; criado_em: string; beneficios: { id: string; nome: string; preco_centavos: number; efeito: string; ativo: boolean }[] };
type StatusPedido = "AGUARDANDO" | "PAGO" | "RECUSADO" | "EXPIRADO" | "REEMBOLSADO" | "CONTESTADO";
type LinhaPedido = { codigo: string; status: StatusPedido; meio: "pix" | "cartao"; parcelas: number; valor_centavos: number; cupom: string | null; /** Nome da promocao que deu o desconto (spec 067), o de quando vendeu. */ promocao: string | null; desconto_centavos: number; comprador: { nome: string; cpf: string; email: string }; checkout: { nome: string; plano_slug: string | null; sem_assinatura: boolean } | null; cobranca: { descricao: string; referencia: string } | null; plataforma: "PROFIT_ONE" | "PROFIT_PRO" | null; motivo: { titulo: string; detalhe: string } | null; criado_em: string; pago_em: string | null };
type DetalhePedido = LinhaPedido & { telefone: string; itens: { plano_slug: string | null; nome: string; plataforma: string | null; valor_centavos: number }[]; pix: { e2e: string | null } | null; cartao: { final: string | null; bandeira: string | null; titular: string | null; titular_documento: string | null } | null; eventos_de_saida: { id: string; tipo: string; entregue_em: string | null; tentativas: number; ultimo_erro: string | null }[]; eventos_do_gateway: { id: string; tipo: string; status: string; erro: string | null }[]; aceite: AceiteDoPedido | null };
/**
 * O aceite que a tentativa guardou (spec 042): com `grupo`, a versao do nucleo e as chaves das caixas marcadas; sem ele,
 * o rotulo antigo da versao (checkout de antes do nucleo). `em` e a hora do clique.
 */
type AceiteDoPedido = { grupo?: number; documentos?: string[]; versao?: string; url?: string; em?: string; ip?: string | null; navegador?: string | null };
/** A linha "Regulamento" do detalhe da venda: a versao e a hora do aceite, como a tentativa guardou. */
function linhaDoAceite(a: AceiteDoPedido | null | undefined): { rotulo: string; valor: string; detalhe?: string } {
  if (!a) return { rotulo: "Regulamento", valor: "Sem aceite (o checkout não pedia)" };
  const versao = a.grupo ? `Versão ${a.grupo}` : a.versao && a.versao !== "nucleo" ? `Versão antiga (${a.versao})` : "Regulamento da marca";
  // As chaves das caixas marcadas ("geral", "EXAME") viram nome de gente: "Geral e Exame".
  const nomes = (a.documentos ?? []).map((c) => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase());
  const docs = a.grupo && nomes.length ? ` (${nomes.length > 1 ? `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}` : nomes[0]})` : "";
  return { rotulo: "Regulamento", valor: `${versao}${docs}`, detalhe: a.em ? `Aceito em ${formatarDataHora(a.em)}${a.ip ? `, IP ${a.ip}` : ""}` : undefined };
}
type EventoDoGateway = { id: string; tipo: string; status: string; pedido: string | null; erro: string | null; recebido_em: string };

const PLATAFORMA = { PROFIT_ONE: "Profit One", PROFIT_PRO: "Profit Pro" } as const;
const MEIO = { pix: "PIX", cartao: "Cartão" } as const;
/** A plataforma da venda na coluna Profit (Vitor, 27/10). */
const PROFIT: Record<string, string> = { PROFIT_ONE: "One", PROFIT_PRO: "Pro" };
const STATUS: Record<StatusPedido, { rotulo: string; tom: Tom }> = {
  AGUARDANDO: { rotulo: "Aguardando", tom: "info" },
  PAGO: { rotulo: "Pago", tom: "sucesso" },
  RECUSADO: { rotulo: "Recusado", tom: "erro" },
  EXPIRADO: { rotulo: "Expirado", tom: "neutro" },
  REEMBOLSADO: { rotulo: "Reembolsado", tom: "alerta" },
  CONTESTADO: { rotulo: "Contestado", tom: "alerta" },
};
const juros = (bps: number) => (bps ? `${(bps / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}% por parcela` : "sem juros");

// ---------------------------------------------------------------- checkouts

/** Nome, plataforma e o que a oferta promete: o que o operador precisa pra achar o checkout certo. */
function resumoDoCheckout(c: Checkout, nomeDoPlano: string | undefined) {
  const plataforma = c.plataforma ? `Só ${PLATAFORMA[c.plataforma]}` : c.adicional_pro_centavos ? "Profit One ou Pro" : null;
  const plano = nomeDoPlano && nomeDoPlano !== c.nome ? `Plano ${nomeDoPlano}` : null;
  const ativos = c.beneficios.filter((b) => b.ativo).map((b) => b.nome);
  const beneficios = ativos.length ? `Benefícios: ${ativos.slice(0, 2).join(", ")}${ativos.length > 2 ? ` e mais ${ativos.length - 2}` : ""}` : null;
  return { apoio: [plano, plataforma].filter(Boolean).join(", "), beneficios };
}

/** "PIX e cartão até 12x" e, com cartao parcelado, os juros; em duas linhas curtas. */
function pagamentoDoCheckout(c: Checkout) {
  const pix = c.meios.includes("pix");
  const cartao = c.meios.includes("cartao");
  const parcelado = cartao && c.max_parcelas > 1;
  const noCartao = parcelado ? `cartão até ${c.max_parcelas}x` : "cartão à vista";
  const linha = pix && cartao ? `PIX e ${noCartao}` : pix ? "Só PIX" : noCartao.charAt(0).toUpperCase() + noCartao.slice(1);
  const taxa = (c.juros_bps_por_parcela / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return { linha, juros: parcelado ? (c.juros_bps_por_parcela ? `${taxa}% de juros por parcela` : "sem juros") : null };
}

export function PaginaCheckouts() {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const planos = usePlanos();
  const lista = useQuery({ queryKey: ["checkouts"], queryFn: () => ler<{ itens: Checkout[] }>(PAG("/v1/admin/checkouts")) });
  const [aba, setAba] = useState<"ativos" | "desativados">("ativos");
  const pagChk = usePagina("pagina", [aba]);
  const [novo, setNovo] = useState(false);
  const [beneficiosDe, setBeneficiosDe] = useState<Checkout | null>(null);
  const [alternar, setAlternar] = useState<Checkout | null>(null);
  const [voltarDe, setVoltarDe] = useState<Checkout | null>(null);
  const [editando, setEditando] = useState<Checkout | null>(null);
  const [feito, setFeito] = useState<{ texto: string; link?: string | null }>();
  const [destaque, setDestaque] = useState<string>();
  const aviso = useRef<HTMLDivElement>(null);
  const itens = lista.data?.itens ?? [];
  // Ativos em ordem alfabetica (agrupa Black, Exame e Fast); desativados, o mais recente primeiro.
  const ativos = itens.filter((c) => c.ativo).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const desativados = itens.filter((c) => !c.ativo).sort((a, b) => b.criado_em.localeCompare(a.criado_em));
  const linhas = aba === "ativos" ? ativos : desativados;
  const nomeDoPlano = (slug: string) => planos.data?.itens.find((p) => p.slug === slug)?.nome;
  const avisar = (texto: string, link?: string | null) => {
    setFeito({ texto, link });
    // A linha pode ter sumido da aba: o foco vai pro aviso, pra quem usa teclado ou leitor de tela saber o que houve.
    setTimeout(() => aviso.current?.focus(), 0);
  };
  const preco = (c: Checkout) => formatarReais(c.preco_centavos);
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Checkouts" descricao="Um link por plano. O comprador paga por PIX ou cartão e recebe o plano sozinho; depois ele agenda o início e a equipe libera com a subconta." acoes={admin ? <Botao onClick={() => { setFeito(undefined); setNovo(true); }}>Novo checkout</Botao> : undefined} />
      {feito && (
        <div ref={aviso} tabIndex={-1} style={{ outline: "none" }}>
          <Aviso tipo="sucesso">
            <Pilha espaco={2}>
              <span>{feito.texto}</span>
              {feito.link && <TextoCopiavel valor={feito.link} rotulo="Copiar link" />}
            </Pilha>
          </Aviso>
        </div>
      )}
      {lista.isLoading ? <Carregando compacto texto="Buscando os checkouts" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Pilha espaco={3}>
          <Abas rotulo="Checkouts" valor={aba} aoMudar={setAba} abas={[{ valor: "ativos", rotulo: "Ativos", contagem: ativos.length }, { valor: "desativados", rotulo: "Desativados", contagem: desativados.length }]} />
          <Tabela
            paginacao={pagChk}
            linhas={linhas}
            chave={(c) => c.id}
            cartao="media"
            destaque={(c) => c.id === destaque}
            vazio={aba === "ativos" ? <Vazio titulo="Nenhum checkout ativo">Crie o primeiro: escolha o plano, o preço e os meios de pagamento.</Vazio> : <Vazio titulo="Nenhum checkout desativado">Quando um checkout for desativado, ele fica aqui e dá pra ativar de novo.</Vazio>}
            colunas={[
              { chave: "checkout", rotulo: "Checkout", celula: (c) => {
                const r = resumoDoCheckout(c, c.plano_slug ? nomeDoPlano(c.plano_slug) : undefined);
                // So a excecao aparece: checkout sem regulamento. Com regulamento, ele pede sempre a versao que estiver no ar no
                // nucleo (spec 042, 3.4), entao nao existe mais "regulamento antigo". Sem assinatura, o regulamento e opcional e
                // nasce desmarcado (spec 036, decisao 5): ali "Sem regulamento" nao e excecao.
                const semRegulamento = !c.termos && !c.sem_assinatura;
                return (
                  <>
                    <strong>{c.nome}</strong>
                    {/* So cobra (spec 036): o selo fica no lugar do plano, pra ninguem procurar matricula que nao existe. */}
                    {c.sem_assinatura && <span className="mtc-tabela__apoio" style={{ marginTop: "var(--e-2)" }}><SeloSemAssinatura /></span>}
                    {r.apoio && <span className="mtc-tabela__apoio">{r.apoio}</span>}
                    {aba === "ativos" && r.beneficios && <span className="mtc-tabela__apoio">{r.beneficios}</span>}
                    {aba === "desativados" && <span className="mtc-tabela__apoio">Criado em {formatarDia(c.criado_em)}</span>}
                    {semRegulamento && <span className="mtc-tabela__apoio"><Selo tom="alerta">Sem regulamento</Selo></span>}
                    {/* Planos a venda da area (15/10): so a excecao aparece, o que esta fora. */}
                    {!c.sem_assinatura && !c.na_area && <span className="mtc-tabela__apoio"><Selo tom="neutro">Fora da área do cliente</Selo></span>}
                    {/* Back redirect (spec 045, 3.10): pra onde o voltar leva. A troca fica ao lado, porque muda com a campanha. */}
                    <span className="mtc-tabela__apoio" data-voltar-para>
                      Back redirect: {c.voltar_para ? <span style={{ overflowWrap: "anywhere" }}>{c.voltar_para}</span> : "Site da marca"}
                      {admin && <>{" "}<button type="button" className="mtc-link" aria-label={`Mudar back redirect do ${c.nome}`} onClick={() => { setFeito(undefined); setVoltarDe(c); }}>Mudar</button></>}
                    </span>
                  </>
                );
              } },
              { chave: "preco", rotulo: "Preço", alinhar: "direita", celula: (c) => <><span className="mtc-mono">{preco(c)}</span>{c.preco_pro_centavos != null && <span className="mtc-tabela__apoio mtc-mono">Pro {formatarReais(c.preco_pro_centavos)}</span>}{c.anunciado_em_dolar && <span className="mtc-tabela__apoio mtc-mono">US$ {(c.anunciado_em_dolar.preco_usd_centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} a {cotacaoPorExtenso(c.anunciado_em_dolar.cotacao)}</span>}</> },
              { chave: "pagamento", rotulo: "Pagamento", celula: (c) => {
                const p = pagamentoDoCheckout(c);
                return <><span style={{ whiteSpace: "nowrap" }}>{p.linha}</span>{p.juros && <span className="mtc-tabela__apoio" style={{ whiteSpace: "nowrap" }}>{p.juros}</span>}</>;
              } },
              { chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (c) => (
                // Quatro acoes nao cabem numa linha em 1280 px (visto no WebKit com o "Editar", 15/10): quebram em duas.
                <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap", maxWidth: 250 }}>
                  {c.ativo && (c.link ? <BotaoCopiar valor={c.link} rotulo="Copiar link" rotuloAcessivel={`Copiar link do ${c.nome}`} /> : <span className="mtc-texto-2">Sem link: falta o endereço da área</span>)}
                  {admin && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Editar ${c.nome}`} onClick={() => { setFeito(undefined); setEditando(c); }}>Editar</Botao>}
                  {admin && c.ativo && !c.sem_assinatura && <Botao variante="fantasma" tamanho="pequeno" aria-label={`Benefícios do ${c.nome}`} onClick={() => setBeneficiosDe(c)}>Benefícios</Botao>}
                  {admin && <Botao variante="fantasma" tamanho="pequeno" aria-label={`${c.ativo ? "Desativar" : "Ativar"} ${c.nome}`} onClick={() => setAlternar(c)}>{c.ativo ? "Desativar" : "Ativar"}</Botao>}
                </div>
              ) },
            ]}
          />
        </Pilha>
      )}
      <NovoCheckout aberto={novo} aoFechar={() => setNovo(false)} aoCriar={async (c) => { await qc.invalidateQueries({ queryKey: ["checkouts"] }); setAba("ativos"); setDestaque(c.id); avisar(`Checkout ${c.nome} criado.`, c.link); }} />
      <DialogoVoltarPara
        checkout={voltarDe}
        aoFechar={() => setVoltarDe(null)}
        aoConcluir={async (c, novo) => {
          setVoltarDe(null);
          setDestaque(c.id);
          await qc.invalidateQueries({ queryKey: ["checkouts"] });
          avisar(novo ? `Back redirect do checkout ${c.nome} salvo. O voltar agora leva pra ${novo}.` : `Back redirect do checkout ${c.nome} tirado. O voltar agora leva pro site da marca.`);
        }}
      />
      <EditarCheckout
        checkout={editando}
        aoFechar={() => setEditando(null)}
        aoConcluir={async (c, mudou) => {
          setEditando(null);
          setDestaque(c.id);
          await qc.invalidateQueries({ queryKey: ["checkouts"] });
          avisar(mudou ? `Checkout ${c.nome} atualizado. O link é o mesmo e já vende assim; as vendas de antes não mudam.` : "Nada mudou no checkout.");
        }}
      />
      <DialogoBeneficiosDoCheckout checkout={beneficiosDe} aoFechar={() => setBeneficiosDe(null)} aoConcluir={() => { setBeneficiosDe(null); void qc.invalidateQueries({ queryKey: ["checkouts"] }); avisar("Benefícios do checkout atualizados."); }} />
      <DialogoDeAcao
        aberto={Boolean(alternar)}
        aoFechar={() => setAlternar(null)}
        titulo={alternar ? `${alternar.ativo ? "Desativar" : "Ativar"} o checkout ${alternar.nome}?` : ""}
        descricao={alternar ? (alternar.ativo ? "Quem abrir o link, pelo site ou pelo WhatsApp, vai ver que ele não existe mais. Compras já feitas não mudam. Dá pra ativar de novo na aba Desativados." : `O link volta a vender por ${preco(alternar)}${alternar.preco_pro_centavos != null ? ` (Pro ${formatarReais(alternar.preco_pro_centavos)})` : ""}.`) : undefined}
        botao={alternar?.ativo ? "Desativar checkout" : "Ativar checkout"}
        variante={alternar?.ativo ? "perigo" : "primario"}
        aoEnviar={() => postar(PAG(`/v1/admin/checkouts/${alternar!.id}/ativo`), { ativo: !alternar!.ativo })}
        aoConcluir={async () => {
          const c = alternar!;
          setAlternar(null);
          await qc.invalidateQueries({ queryKey: ["checkouts"] });
          avisar(c.ativo ? `Checkout ${c.nome} desativado. Ele agora está na aba Desativados.` : `Checkout ${c.nome} ativado. Ele voltou pra aba Ativos.`);
        }}
      />
      <LinksAntigos />
      <CredenciaisDoSite />
    </Pilha>
  );
}

type LinkAntigo = { token: string; destino: string; nota: string | null; criado_em: string; criado_por: string | null };

/**
 * Links de checkout do sistema antigo (spec 009, T12). O painel do sistema antigo montava `.../checkout/<token>` e
 * esses links seguem circulando em e-mail, WhatsApp e favorito. Aqui a equipe diz pra onde cada um vai; quem abrir o
 * link velho e levado sozinho. E de/para puro: nenhuma regra de marca.
 */
function LinksAntigos() {
  const pagLinks = usePagina("pagina_links");
  const { ler, postar, apagar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const [feito, setFeito] = useState<string>();
  const [erro, setErro] = useState<string>();
  const lista = useQuery({ queryKey: ["checkouts-antigos"], queryFn: () => ler<{ itens: LinkAntigo[] }>(PAG("/v1/admin/checkouts-antigos")) });
  // Uma linha por link: "<token ou link antigo> <destino> [nota]". O token aceita o link inteiro, pra colar do painel antigo.
  const linhas = texto.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [bruto, destino, ...resto] = l.split(/[\s,;]+/);
    return { token: (bruto ?? "").split("/").filter(Boolean).pop() ?? "", destino: destino ?? "", nota: resto.join(" ") || null };
  });
  const validas = linhas.filter((i) => i.token.length >= 6 && (i.destino.startsWith("/") || i.destino.startsWith("https://")));
  return (
    <Secao
      rotulo="Transição"
      titulo="Links do sistema antigo"
      acoes={pode("ADMIN") ? <Botao tamanho="pequeno" variante="secundario" onClick={() => { setTexto(""); setErro(undefined); setFeito(undefined); setAberto(true); }}>Carregar links</Botao> : undefined}
    >
      <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>
        Quem abrir um link antigo é levado pro endereço novo, sem ver erro. Vale pros links que a equipe já mandou por e-mail e WhatsApp.
      </span>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {lista.isLoading ? <Carregando compacto /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          paginacao={pagLinks}
          linhas={lista.data?.itens ?? []}
          chave={(a) => a.token}
          vazio={<Vazio titulo="Nenhum link antigo na lista">Enquanto a lista estiver vazia, link antigo mostra "este link não existe mais".</Vazio>}
          colunas={[
            { chave: "token", rotulo: "Link antigo", celula: (a) => <span className="mtc-mono">{a.token}</span> },
            { chave: "destino", rotulo: "Vai pra", celula: (a) => <><span className="mtc-mono" style={{ overflowWrap: "anywhere" }}>{a.destino}</span>{a.nota && <span className="mtc-tabela__apoio">{a.nota}</span>}</> },
            { chave: "quando", rotulo: "Carregado em", celula: (a) => <span className="mtc-mono">{formatarDataHora(a.criado_em)}</span> },
            ...(pode("ADMIN") ? [{ chave: "acoes", rotulo: "", celula: (a: LinkAntigo) => <Botao tamanho="pequeno" variante="fantasma" onClick={async () => { setErro(undefined); try { await apagar(PAG(`/v1/admin/checkouts-antigos/${encodeURIComponent(a.token)}`)); await qc.invalidateQueries({ queryKey: ["checkouts-antigos"] }); } catch (e) { setErro(erroDe(e)); } }}>Tirar</Botao> }] : []),
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Carregar links do sistema antigo"
        descricao="Uma linha por link: o link antigo (ou só o token), um espaço, e pra onde ele vai. O que já estiver na lista tem o destino trocado."
        botao={`Gravar ${validas.length} ${validas.length === 1 ? "link" : "links"}`}
        podeEnviar={validas.length > 0}
        aoEnviar={() => postar(PAG("/v1/admin/checkouts-antigos"), { itens: validas })}
        aoConcluir={() => { setAberto(false); setFeito(`${validas.length} ${validas.length === 1 ? "link carregado" : "links carregados"}.`); void qc.invalidateQueries({ queryKey: ["checkouts-antigos"] }); }}
      >
        <CampoTextoLongo
          rotulo="Links"
          valor={texto}
          aoMudar={setTexto}
          linhas={8}
          maxLength={40_000}
          dica="Ex.: app.mtcprop.com.br/checkout/abc123 /checkout/xyz789 Exame Start"
        />
        {Boolean(linhas.length) && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{validas.length} de {linhas.length} linhas prontas. O destino precisa começar com / ou https://.</span>}
      </DialogoDeAcao>
    </Secao>
  );
}

/** O que `GET /v1/publico/regulamentos/para-compra` devolve: a versao atual e os documentos que esta compra aceita. */
type RegulamentoParaCompra = { grupo: number; rotulo: string | null; publicado_em: string; documentos: { chave: string; titulo: string; sha256: string; tipo: string; url: string }[] };
/** 404: nada publicado ainda no nucleo (o checkout cai no regulamento da marca); 409: a familia do plano nao tem regulamento. */
type ConsultaDoRegulamento = { ok: true; dados: RegulamentoParaCompra } | { ok: false; status: 404 | 409; erro: string };

/** Valor da opcao "Sem assinatura (só pagamento)" no campo Plano: nao e plano, vira `plano_slug: null` no envio. */
const SEM_ASSINATURA = "__sem_assinatura__";

/** Sem plano (ou nome curto demais pra comparar) nao ha o que conferir. */
const nomeBateComPlano = (nome: string, plano: string) => !plano || nome.trim().length < 2 || palavrasDoPlanoQueFaltam(nome, plano).length === 0;

/**
 * Nome do checkout que nao bate com o plano (`nomeDoCheckout.ts`): o que o comprador le, o que ele recebe e a caixa que
 * libera o botao. A confirmacao vale pra este nome com este plano; mudou um dos dois, pergunta de novo.
 */
function NomeContraPlano({ nome, plano, confirmado, aoConfirmar }: { nome: string; plano: string; confirmado: boolean; aoConfirmar: (sim: boolean) => void }) {
  if (nomeBateComPlano(nome, plano)) return null;
  const faltam = palavrasDoPlanoQueFaltam(nome, plano);
  return (
    <Pilha espaco={2}>
      <Aviso tipo="alerta">
        O nome não bate com o plano. O comprador vai ver <strong>{nome.trim()}</strong>, mas quem pagar recebe o plano <strong>{plano}</strong>: {emLista(faltam)} {faltam.length > 1 ? "não aparecem" : "não aparece"} no nome. Confira se escolheu o plano certo.
      </Aviso>
      <CampoCaixa marcado={confirmado} aoMudar={aoConfirmar}>O plano está certo, pode usar este nome</CampoCaixa>
    </Pilha>
  );
}

function NovoCheckout({ aberto, aoFechar, aoCriar }: { aberto: boolean; aoFechar: () => void; aoCriar: (c: Checkout) => Promise<void> }) {
  const { postar } = useApi();
  const planos = usePlanos();
  const { ler } = useApi();
  const { modulos } = useAmbiente();
  const plataformaProfit = temPlataformaProfit(modulos);
  // A mensalidade e do plano (spec 007, M6; Vitor, 16/09): o checkout so mostra a que o plano tem.
  const temMensalidade = modulos.includes("mensalidade-manual");
  // "ESCOLHA" (o padrao, como no legado): o comprador escolhe no checkout, Profit One no preco e Profit Pro com o adicional.
  const vazio = { plano: "", nome: "", plataforma: "ESCOLHA" as "ESCOLHA" | "PROFIT_ONE" | "PROFIT_PRO" | "NENHUMA", preco: 0, precoUsd: 0, adicionalPro: 13_010, pix: true, cartao: true, parcelas: "12", juros: "1,67", regulamento: true, beneficios: [] as string[], voltarPara: "", naArea: true, nomeConfirmado: "" };
  const [f, setF] = useState(vazio);
  // Checkout "Sem assinatura" (spec 036): so cobra. Sem plano nao ha plataforma, Profit Pro, beneficio nem mensalidade.
  const semAssinatura = f.plano === SEM_ASSINATURA;
  const plano = semAssinatura ? "" : f.plano;
  const nomeDoPlano = (plano && planos.data?.itens.find((p) => p.slug === plano)?.nome) || "";
  const chaveDoNome = `${nomeDoPlano}|${f.nome.trim()}`;
  // Spec 046, 2.1: com plano, o regulamento e obrigatorio (o `pagamentos` recusa sem); a caixa fica marcada e travada.
  // So o "Sem assinatura" escolhe.
  const comRegulamento = !semAssinatura || f.regulamento;
  // A resposta da criacao, lida no fechamento do dialogo (estado do React ainda nao teria atualizado).
  const criado = useRef<Checkout | null>(null);
  const jurosBps = Math.round(Number(f.juros.replace(",", ".")) * 100);
  /**
   * O que este ambiente sabe fazer (spec 020, decisao 4). A moeda da vitrine decide se o preco se digita em dolar; sem
   * resposta, o painel segue em real, que e o caso da Prop e o unico que existia antes.
   */
  const capacidades = useQuery({ queryKey: ["capacidades-do-pagamentos"], queryFn: () => ler<{ moeda_vitrine: "BRL" | "USD"; recorrencia: boolean; gateway: boolean }>(PAG("/v1/admin/capacidades")) });
  const emDolar = capacidades.data?.moeda_vitrine === "USD";
  const plataformaPadrao = !plataformaProfit ? "NENHUMA" : emDolar ? "PROFIT_ONE" : "ESCOLHA";
  // Spec 050: a cotacao vem sozinha, a PTAX do Banco Central que o servico guardou. Aqui a conta e a MESMA
  // (`@mtc/contratos`), so pra mostrar quanto o cliente paga hoje; quem calcula e o servico, e a rotina reprecifica.
  const cotacaoDoDia = useQuery({ queryKey: ["cotacao-do-dolar"], queryFn: () => ler<{ cotacao: string; publicada_em: string; fonte: string }>(PAG("/v1/admin/cotacao-do-dolar")), enabled: emDolar, retry: false, staleTime: 10 * 60_000 });
  const cotacaoDeHoje = cotacaoDoDia.data?.cotacao ?? "";
  const precoEmReal = emDolar && f.precoUsd > 0 && cotacaoValida(cotacaoDeHoje) ? realDoDolar(f.precoUsd, cotacaoDeHoje) : null;
  const precoCobrado = emDolar ? (precoEmReal ?? 0) : f.preco;
  // O regulamento que o cliente aceita (spec 042, 3.4): o checkout "exige o regulamento vigente" e a pagina pergunta ao
  // nucleo, na hora da compra, qual versao esta no ar. Aqui so se confere, antes de criar, que existe regulamento pra
  // familia deste plano (3.8). Quem decide a familia e os documentos e o backend; sem plano, vale o geral sozinho.
  const regulamento = useQuery({
    queryKey: ["regulamento-para-compra", plano],
    enabled: aberto && comRegulamento && Boolean(f.plano),
    retry: false,
    staleTime: 0,
    queryFn: async (): Promise<ConsultaDoRegulamento> => {
      try {
        return { ok: true, dados: await ler<RegulamentoParaCompra>(`/v1/publico/regulamentos/para-compra${plano ? `?plano=${encodeURIComponent(plano)}` : ""}`) };
      } catch (e) {
        if (e instanceof ErroApi && (e.status === 404 || e.status === 409)) return { ok: false, status: e.status, erro: e.corpo?.erro ?? "" };
        throw e;
      }
    },
  });
  const consulta = regulamento.data;
  // Nada publicado ainda (404) nao trava: a pagina do checkout cai no regulamento da marca ate a primeira versao.
  const regulamentoPronto = Boolean(consulta && (consulta.ok || consulta.status === 404));
  // O texto que o comprador vai ler: regra e valores sao do plano (backend).
  const texto = useQuery({ queryKey: ["texto-da-mensalidade", plano], queryFn: () => ler<{ texto: string | null }>(`/v1/planos/${plano}/mensalidade`), enabled: temMensalidade && Boolean(plano) });
  // Aceite marcado e regulamento que nao da pra montar TRAVA a criacao (spec 020, decisao 7): um checkout sem aceite e
  // pior que o do sistema antigo, e o erro so apareceria na primeira compra.
  const pronto = Boolean(f.plano) && f.nome.trim().length >= 2 && (emDolar ? f.precoUsd >= 100 : precoCobrado >= 100) && (f.plataforma !== "ESCOLHA" || f.adicionalPro > 0) && (f.pix || f.cartao) && Number.isFinite(jurosBps) && jurosBps >= 0 && (!comRegulamento || regulamentoPronto) && campoDeVoltarParaOk(f.voltarPara) && (nomeBateComPlano(f.nome, nomeDoPlano) || f.nomeConfirmado === chaveDoNome);
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Novo checkout"
      descricao="O link nasce ativo. Nome, preço, meios e parcelas dá pra editar depois no mesmo link; plano e plataforma não mudam (pra isso, crie outro)."
      botao="Criar checkout"
      podeEnviar={pronto}
      aoAbrir={() => setF({ ...vazio, plataforma: plataformaPadrao })}
      aoEnviar={async () => { criado.current = await postar<Checkout>(PAG("/v1/admin/checkouts"), {
        plano_slug: semAssinatura ? null : f.plano,
        nome: f.nome.trim(),
        plataforma: f.plataforma === "NENHUMA" || f.plataforma === "ESCOLHA" ? null : f.plataforma,
        // Em dolar mandamos so o que foi anunciado: a cotacao e a PTAX do dia, e o servico calcula o real (spec 050).
        ...(emDolar ? { preco_usd_centavos: f.precoUsd } : { preco_centavos: f.preco }),
        ...(f.plataforma === "ESCOLHA" ? { adicional_pro_centavos: f.adicionalPro } : {}),
        meios: [...(f.pix ? ["pix"] : []), ...(f.cartao ? ["cartao"] : [])],
        max_parcelas: Number(f.parcelas),
        juros_bps_por_parcela: jurosBps,
        // "Exige o regulamento vigente" (spec 042): a versao nao fica gravada no checkout; a pagina pega a atual do nucleo.
        termos_url: comRegulamento ? "/regulamento" : null,
        termos_versao: comRegulamento ? "nucleo" : null,
        beneficio_ids: semAssinatura ? [] : f.beneficios,
        // Back redirect (spec 045, 3.10): vazio vai nulo e o voltar leva pro site da marca.
        voltar_para: voltarParaDoCampo(f.voltarPara),
        // Planos a venda da area (15/10): sem plano nao ha o que vender la.
        na_area: semAssinatura ? false : f.naArea,
      }); }}
      aoConcluir={() => { if (criado.current) void aoCriar(criado.current); }}
    >
      <CampoSelecao
        rotulo="Plano"
        valor={f.plano}
        vazio="Escolha o plano"
        required
        aoMudar={(escolha) => setF(escolha === SEM_ASSINATURA
          // Regulamento nasce desmarcado: quem paga uma taxa ja aceitou o regulamento quando comprou o plano (decisao 5).
          ? { ...f, plano: escolha, plataforma: "NENHUMA", regulamento: false, beneficios: [] }
          : { ...f, plano: escolha, nome: f.nome || (planos.data?.itens.find((p) => p.slug === escolha)?.nome ?? ""), ...(semAssinatura ? { plataforma: plataformaPadrao, regulamento: true } : {}) })}
        opcoes={[{ valor: SEM_ASSINATURA, rotulo: "Sem assinatura (só pagamento)" }, ...(planos.data?.itens ?? []).filter((p) => p.ativo).map((p) => ({ valor: p.slug, rotulo: p.nome }))]}
      />
      {semAssinatura && <Aviso tipo="info">Este link só cobra. Quem pagar não recebe plano, conta nem acesso: a equipe faz o que for preciso à mão.</Aviso>}
      <Campo rotulo="Nome no checkout" valor={f.nome} aoMudar={(nome) => setF({ ...f, nome })} dica="O que o comprador vê no topo do pagamento." />
      <NomeContraPlano nome={f.nome} plano={nomeDoPlano} confirmado={f.nomeConfirmado === chaveDoNome} aoConfirmar={(sim) => setF({ ...f, nomeConfirmado: sim ? chaveDoNome : "" })} />
      {/* Deixar o cliente escolher a plataforma soma um adicional EM REAL; com o preco base em dolar, o checkout
          misturaria duas moedas. Entao a escolha so existe onde a vitrine e em real (spec 020, decisao 4). */}
      {/* A plataforma e da Nelogica V1, que so a Prop liga: na Global ela nao existe e o campo nao aparece.
          Antes isto era decidido pela MOEDA, o que traria o campo de volta numa vitrine em real da Global. */}
      {plataformaProfit && !semAssinatura && <Segmentado rotulo="Plataforma" nome="plataforma" valor={f.plataforma} aoMudar={(plataforma) => setF({ ...f, plataforma })} opcoes={[...(emDolar ? [] : [{ valor: "ESCOLHA" as const, rotulo: "O cliente escolhe" }]), { valor: "PROFIT_ONE" as const, rotulo: "Só Profit One" }, { valor: "PROFIT_PRO" as const, rotulo: "Só Profit Pro" }, { valor: "NENHUMA" as const, rotulo: "Não se aplica" }]} />}
      {emDolar ? (
        <>
          <CampoDinheiro rotulo="Preço anunciado" moeda="USD" centavos={f.precoUsd} aoMudar={(precoUsd) => setF({ ...f, precoUsd })} dica="O produto é anunciado em dólar e cobrado em real." />
          {cotacaoDoDia.isError ? (
            <Aviso tipo="alerta">Ainda não há cotação do Banco Central guardada. Dá pra criar assim mesmo: o serviço busca a PTAX na hora.</Aviso>
          ) : cotacaoDoDia.data ? (
            <span className="mtc-campo__dica">
              Cotação do dólar: <strong className="mtc-mono">{cotacaoPorExtenso(cotacaoDoDia.data.cotacao)}</strong>, PTAX do Banco Central de {formatarDia(cotacaoDoDia.data.publicada_em)}.{" "}
              {precoEmReal ? <>O cliente paga <strong className="mtc-mono">{formatarReais(precoEmReal)}</strong> hoje. </> : null}O preço em real acompanha cada cotação nova do Banco Central; o dólar anunciado não muda.
            </span>
          ) : null}
        </>
      ) : (
        <CampoDinheiro rotulo={f.plataforma === "ESCOLHA" ? "Preço com Profit One" : "Preço"} centavos={f.preco} aoMudar={(preco) => setF({ ...f, preco })} />
      )}
      {f.plataforma === "ESCOLHA" && (
        <CampoDinheiro rotulo="Adicional do Profit Pro" centavos={f.adicionalPro} aoMudar={(adicionalPro) => setF({ ...f, adicionalPro })} dica={f.preco > 0 && f.adicionalPro > 0 ? `Com Profit Pro o comprador paga ${formatarReais(f.preco + f.adicionalPro)}. O legado usa R$ 130,10.` : "Somado ao preço quando o comprador escolhe o Profit Pro. O legado usa R$ 130,10."} />
      )}
      {temMensalidade && plano && texto.data && (
        texto.data.texto
          ? <span className="mtc-campo__dica">Mensalidade do plano (não aparece no checkout): {texto.data.texto}</span>
          : <Aviso tipo="alerta">Este plano está sem mensalidade. Configure em Cadastros, Planos: sem ela, quem comprar fica sem valor e a equipe informa na matrícula.</Aviso>
      )}
      {!semAssinatura && <EscolhaDeBeneficios escolhidos={f.beneficios} aoMudar={(beneficios) => setF({ ...f, beneficios })} />}
      <CampoCaixa marcado={f.pix} aoMudar={(pix) => setF({ ...f, pix })}>Aceita PIX</CampoCaixa>
      <CampoCaixa marcado={f.cartao} aoMudar={(cartao) => setF({ ...f, cartao })}>Aceita cartão de crédito</CampoCaixa>
      {f.cartao && (
        <>
          <CampoSelecao rotulo="Parcelas no cartão" valor={f.parcelas} aoMudar={(parcelas) => setF({ ...f, parcelas })} opcoes={Array.from({ length: 12 }, (_, i) => ({ valor: String(i + 1), rotulo: i === 0 ? "Só à vista" : `Até ${i + 1}x` }))} />
          <Campo rotulo="Juros por parcela (%)" valor={f.juros} aoMudar={(juros) => setF({ ...f, juros })} inputMode="decimal" mono dica="0 = parcelado sem juros. O legado usava 1,67% por parcela. À vista nunca tem juros." />
        </>
      )}
      <Pilha espaco={2}>
        <CampoCaixa marcado={comRegulamento} disabled={!semAssinatura} aoMudar={(regulamento) => setF({ ...f, regulamento })}>O cliente aceita o regulamento antes de pagar</CampoCaixa>
        {!semAssinatura && <span className="mtc-campo__dica" data-regulamento-obrigatorio>Checkout com plano sempre pede o regulamento.</span>}
        {comRegulamento && (
          !f.plano ? (
            <span className="mtc-campo__dica">Escolha o plano pra ver qual regulamento vale.</span>
          ) : regulamento.isLoading ? (
            <span className="mtc-campo__dica">Conferindo o regulamento deste plano.</span>
          ) : consulta?.ok ? (
            <span className="mtc-campo__dica" data-regulamento-do-checkout>
              {consulta.dados.documentos.map((d) => d.titulo).join(" e ")}, na versão que estiver no ar na hora da compra (hoje, versão {consulta.dados.grupo}{consulta.dados.rotulo ? `, ${consulta.dados.rotulo}` : ""}). Publicou versão nova em Cadastros, Regulamentos, este checkout passa a pedir a nova sozinho.
            </span>
          ) : consulta?.status === 409 ? (
            <Aviso tipo="erro">{consulta.erro || "A família deste plano ainda não tem regulamento publicado. Publique antes de vender este plano."}</Aviso>
          ) : consulta?.status === 404 ? (
            <Aviso tipo="alerta">Ainda não há versão publicada em Cadastros, Regulamentos. Até a primeira, o checkout mostra o regulamento da marca; publicada, passa a pedir a versão nova sozinho.</Aviso>
          ) : (
            <Aviso tipo="alerta">Não consegui conferir o regulamento agora. Sem isso a criação fica travada: tente de novo em instantes{semAssinatura ? ", ou desmarque acima se este checkout é mesmo sem regulamento" : ""}.</Aviso>
          )
        )}
      </Pilha>
      {!semAssinatura && (
        <Pilha espaco={2}>
          <CampoCaixa marcado={f.naArea} aoMudar={(naArea) => setF({ ...f, naArea })}>Aparece na área do cliente</CampoCaixa>
          <span className="mtc-campo__dica">Em Comprar plano, dentro da área. Desmarque pra oferta que só vale com o link (campanha, preço combinado).</span>
        </Pilha>
      )}
      {/* Opcional, com plano ou sem assinatura: a pagina de uma oferta, por exemplo. Da pra trocar depois na lista. */}
      <CampoVoltarPara valor={f.voltarPara} aoMudar={(voltarPara) => setF({ ...f, voltarPara })} />
    </DialogoDeAcao>
  );
}

type Alteracao = { id: string; por: string; em: string; antes: Record<string, unknown>; depois: Record<string, unknown> };
const CAMPO_ALTERADO: Record<string, string> = { nome: "Nome", preco_centavos: "Preço", preco_usd_centavos: "Preço anunciado", adicional_pro_centavos: "Adicional do Profit Pro", meios: "Meios", max_parcelas: "Parcelas", juros_bps_por_parcela: "Juros", na_area: "Na área do cliente" };
/** "R$ 297,00", "PIX e cartão", "até 12x", "1,67%": o valor do jeito que a tela mostra. */
function valorAlterado(campo: string, v: unknown): string {
  if (v === null || v === undefined) return "nada";
  if (campo === "preco_usd_centavos") return `US$ ${(Number(v) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
  if (campo.endsWith("_centavos")) return formatarReais(Number(v));
  if (campo === "meios") return (v as string[]).map((m) => MEIO[m as keyof typeof MEIO] ?? m).join(" e ");
  if (campo === "max_parcelas") return Number(v) === 1 ? "à vista" : `até ${v}x`;
  if (campo === "juros_bps_por_parcela") return juros(Number(v));
  if (campo === "na_area") return v ? "sim" : "não";
  return String(v);
}

/**
 * Editar o checkout (Vitor, 15/10; no sistema antigo era a acao mais usada: 90 edicoes em 90 dias contra 5 criacoes).
 * O link e o mesmo; o pedido guarda o valor da venda, entao o que ja foi vendido nao muda. Plano, plataforma fixa,
 * regulamento e moeda nao se editam. Embaixo, o que ja mudou neste checkout, com quem e quando.
 */
function EditarCheckout({ checkout: c, aoFechar, aoConcluir }: { checkout: Checkout | null; aoFechar: () => void; aoConcluir: (c: Checkout, mudou: boolean) => Promise<void> }) {
  const { ler, por } = useApi();
  const planos = usePlanos();
  const emDolar = Boolean(c?.anunciado_em_dolar);
  const escolha = Boolean(c && c.adicional_pro_centavos !== null);
  const doCheckout = (x: Checkout) => ({ naArea: x.na_area, nome: x.nome, preco: x.preco_centavos, precoUsd: x.anunciado_em_dolar?.preco_usd_centavos ?? 0, adicionalPro: x.adicional_pro_centavos ?? 0, pix: x.meios.includes("pix"), cartao: x.meios.includes("cartao"), parcelas: String(x.max_parcelas), juros: (x.juros_bps_por_parcela / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 }), nomeConfirmado: "" });
  const [f, setF] = useState(() => (c ? doCheckout(c) : null));
  const salvo = useRef<{ c: Checkout; mudou: boolean } | null>(null);
  const historico = useQuery({ queryKey: ["alteracoes-do-checkout", c?.id], queryFn: () => ler<{ itens: Alteracao[] }>(PAG(`/v1/admin/checkouts/${c!.id}/alteracoes`)), enabled: Boolean(c) });
  const nomes = useQuery({ queryKey: ["nomes-dos-administradores"], queryFn: () => ler<{ itens: { id: string; nome: string }[] }>("/v1/admin/administradores/nomes"), enabled: Boolean(c), staleTime: 5 * 60_000 });
  const quem = (por: string) => nomes.data?.itens.find((o) => `operador:${o.id}` === por)?.nome ?? "Administrador";
  const nomeDoPlano = (c && !c.sem_assinatura && planos.data?.itens.find((p) => p.slug === c.plano_slug)?.nome) || "";
  const chaveDoNome = `${nomeDoPlano}|${f?.nome.trim() ?? ""}`;
  const jurosBps = f ? Math.round(Number(f.juros.replace(",", ".")) * 100) : 0;
  const cotacao = c?.anunciado_em_dolar?.cotacao;
  const precoEmReal = f && emDolar && cotacao && f.precoUsd >= 100 ? realDoDolar(f.precoUsd, cotacao) : null;
  const pronto = Boolean(f && f.nome.trim().length >= 2 && (emDolar ? f.precoUsd >= 100 : f.preco >= 100) && (!escolha || f.adicionalPro > 0) && (f.pix || f.cartao) && Number.isFinite(jurosBps) && jurosBps >= 0 && jurosBps <= 1000 && (nomeBateComPlano(f.nome, nomeDoPlano) || f.nomeConfirmado === chaveDoNome));
  return (
    <DialogoDeAcao
      aberto={Boolean(c)}
      aoFechar={aoFechar}
      titulo={c ? `Editar ${c.nome}` : ""}
      descricao="O link continua o mesmo e já vende com o que você salvar. As vendas de antes ficam com o valor delas. Plano e plataforma não mudam aqui: pra isso, crie outro checkout."
      botao="Salvar"
      podeEnviar={pronto}
      aoAbrir={() => { if (c) setF(doCheckout(c)); salvo.current = null; }}
      aoEnviar={async () => {
        const r = await por<Checkout & { mudou: boolean }>(PAG(`/v1/admin/checkouts/${c!.id}`), {
          nome: f!.nome.trim(),
          ...(emDolar ? { preco_usd_centavos: f!.precoUsd } : { preco_centavos: f!.preco }),
          ...(escolha ? { adicional_pro_centavos: f!.adicionalPro } : {}),
          meios: [...(f!.pix ? ["pix"] : []), ...(f!.cartao ? ["cartao"] : [])],
          max_parcelas: f!.cartao ? Number(f!.parcelas) : c!.max_parcelas,
          juros_bps_por_parcela: f!.cartao ? jurosBps : c!.juros_bps_por_parcela,
          ...(c!.sem_assinatura ? {} : { na_area: f!.naArea }),
        });
        salvo.current = { c: r, mudou: r.mudou };
      }}
      aoConcluir={() => { if (salvo.current) void aoConcluir(salvo.current.c, salvo.current.mudou); }}
    >
      {c && f && (
        <>
          <Fatos itens={[{ rotulo: "Plano", valor: c.sem_assinatura ? "Sem assinatura" : (planos.data?.itens.find((p) => p.slug === c.plano_slug)?.nome ?? c.plano_slug ?? "") }, { rotulo: "Plataforma", valor: c.plataforma ? PLATAFORMA[c.plataforma] : escolha ? "O cliente escolhe" : "Não se aplica" }]} />
          <Campo rotulo="Nome no checkout" valor={f.nome} aoMudar={(nome) => setF({ ...f, nome })} dica="O que o comprador vê no topo do pagamento." />
          <NomeContraPlano nome={f.nome} plano={nomeDoPlano} confirmado={f.nomeConfirmado === chaveDoNome} aoConfirmar={(sim) => setF({ ...f, nomeConfirmado: sim ? chaveDoNome : "" })} />
          {emDolar ? (
            <>
              <CampoDinheiro rotulo="Preço anunciado" moeda="USD" centavos={f.precoUsd} aoMudar={(precoUsd) => setF({ ...f, precoUsd })} />
              <span className="mtc-campo__dica">
                Pela cotação de <strong className="mtc-mono">{cotacaoPorExtenso(cotacao!)}</strong>{precoEmReal ? <>, o cliente paga <strong className="mtc-mono">{formatarReais(precoEmReal)}</strong></> : null}. O real continua acompanhando cada cotação nova do Banco Central.
              </span>
            </>
          ) : (
            <CampoDinheiro rotulo={escolha ? "Preço com Profit One" : "Preço"} centavos={f.preco} aoMudar={(preco) => setF({ ...f, preco })} />
          )}
          {escolha && <CampoDinheiro rotulo="Adicional do Profit Pro" centavos={f.adicionalPro} aoMudar={(adicionalPro) => setF({ ...f, adicionalPro })} dica={f.preco > 0 && f.adicionalPro > 0 ? `Com Profit Pro o comprador paga ${formatarReais(f.preco + f.adicionalPro)}.` : undefined} />}
          <CampoCaixa marcado={f.pix} aoMudar={(pix) => setF({ ...f, pix })}>Aceita PIX</CampoCaixa>
          <CampoCaixa marcado={f.cartao} aoMudar={(cartao) => setF({ ...f, cartao })}>Aceita cartão de crédito</CampoCaixa>
          {f.cartao && (
            <>
              <CampoSelecao rotulo="Parcelas no cartão" valor={f.parcelas} aoMudar={(parcelas) => setF({ ...f, parcelas })} opcoes={Array.from({ length: 12 }, (_, i) => ({ valor: String(i + 1), rotulo: i === 0 ? "Só à vista" : `Até ${i + 1}x` }))} />
              <Campo rotulo="Juros por parcela (%)" valor={f.juros} aoMudar={(juros) => setF({ ...f, juros })} inputMode="decimal" mono dica="0 = parcelado sem juros. À vista nunca tem juros." />
            </>
          )}
          {!c.sem_assinatura && (
            <Pilha espaco={2}>
              <CampoCaixa marcado={f.naArea} aoMudar={(naArea) => setF({ ...f, naArea })}>Aparece na área do cliente</CampoCaixa>
              <span className="mtc-campo__dica">Em Comprar plano, dentro da área. Fora dela, o link continua vendendo do mesmo jeito.</span>
            </Pilha>
          )}
          <Pilha espaco={2}>
            <span className="mtc-rotulo">Alterações</span>
            {historico.isLoading ? <span className="mtc-campo__dica">Buscando as alterações.</span> : !historico.data?.itens.length ? <span className="mtc-campo__dica">Este checkout ainda não foi alterado.</span> : (
              <ul aria-label="Alterações do checkout" style={{ margin: 0, paddingLeft: "var(--e-4)", display: "grid", gap: "var(--e-2)" }}>
                {historico.data.itens.slice(0, 10).map((a) => (
                  <li key={a.id} className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>
                    <strong>{formatarDataHora(a.em)}</strong>, {quem(a.por)}: {Object.keys(a.depois).map((k) => `${CAMPO_ALTERADO[k] ?? k} de ${valorAlterado(k, a.antes[k])} para ${valorAlterado(k, a.depois[k])}`).join("; ")}.
                  </li>
                ))}
              </ul>
            )}
          </Pilha>
        </>
      )}
    </DialogoDeAcao>
  );
}

// ---------------------------------------------------------------- vendas e pedidos

type Aba = "vendas" | "pedidos" | "eventos" | "antigo" | "bloqueados" | "guru";
const ABAS: Aba[] = ["vendas", "pedidos", "eventos", "antigo", "bloqueados", "guru"];
type CpfBloqueado = { cpf: string; motivo: string; bloqueado_por: string; bloqueado_em: string; desbloqueado_em: string | null; desbloqueado_por: string | null; ativo: boolean };

export function PaginaVendas() {
  const [params, setParams] = useSearchParams();
  // A aba mora no endereco (voltar e atualizar mantem a aba e a pagina), e trocar de aba volta pra pagina 1 na mesma
  // navegacao (spec 058, Fase 2).
  const aba = (ABAS.includes(params.get("aba") as Aba) ? params.get("aba") : "vendas") as Aba;
  const mudarAba = (v: Aba) => { const p = new URLSearchParams(params); if (v === "vendas") p.delete("aba"); else p.set("aba", v); p.delete("pagina"); p.delete("pagina_cpfs"); setParams(p, { replace: true }); };
  const [aberto, setAberto] = useState<string | null>(null);
  const { ler } = useApi();
  // So a contagem da aba, do servidor (antes eram as linhas que chegavam, e passava de 200 sem dizer).
  const naoTratados = useQuery({ queryKey: ["eventos-do-gateway", "NAO_TRATADO", "contagem"], queryFn: () => ler<{ total: number }>(PAG("/v1/admin/eventos-do-gateway?status=NAO_TRATADO&pagina=1&por_pagina=1")) });
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Vendas e pedidos" descricao="O que entrou pelo checkout. Vendas: só as pagas. Pedidos: todas as tentativas, com o motivo quando a operadora recusa." />
      <Abas rotulo="Vendas" valor={aba} aoMudar={mudarAba} abas={[{ valor: "vendas", rotulo: "Vendas" }, { valor: "pedidos", rotulo: "Pedidos" }, { valor: "eventos", rotulo: "Não reconhecidos", contagem: naoTratados.data?.total || undefined }, { valor: "antigo", rotulo: "Do sistema antigo" }, { valor: "bloqueados", rotulo: "CPFs bloqueados" }, { valor: "guru", rotulo: "Guru" }]} />
      {aba === "vendas" ? <ListaDeVendas aoAbrir={setAberto} /> : aba === "pedidos" ? <ListaDePedidos aoAbrir={setAberto} /> : aba === "eventos" ? <EventosNaoTratados /> : aba === "antigo" ? <EventosDeOutroSistema /> : aba === "guru" ? <AbaGuru /> : <CpfsBloqueados />}
      <DetalheDoPedido codigo={aberto} aoFechar={() => setAberto(null)} />
    </Pilha>
  );
}

/**
 * As colunas das listas de vendas e de pedidos. Vitor, 27/10: o "Quando" com a hora embaixo da data, o pedido cortado
 * com reticencias (inteiro no detalhe e ao passar o mouse), a coluna Profit (One ou Pro) onde a plataforma existe, e o
 * Status so nos Pedidos: Vendas sao so as pagas (a reembolsada e a contestada ficam nos Pedidos).
 */
function colunasDoPedido(aoAbrir: (c: string) => void, { profit, status }: { profit: boolean; status: boolean }) {
  return [
    { chave: "quando", rotulo: "Quando", celula: (p: LinhaPedido) => { const [dia, hora] = formatarDataHora(p.pago_em ?? p.criado_em).split(" às "); return <><span className="mtc-mono">{dia}</span><span className="mtc-tabela__apoio mtc-mono">{hora}</span></>; } },
    { chave: "comprador", rotulo: "Comprador", celula: (p: LinhaPedido) => <><strong>{p.comprador.nome}</strong><span className="mtc-tabela__apoio mtc-mono">{p.comprador.cpf}</span></> },
    // Compra de plano (checkout) ou cobranca de quem ja e trader (mensalidade, portao do avanco).
    // Checkout que so cobra (spec 036): o selo "Sem assinatura" no lugar do plano, que o servico manda pronto.
    { chave: "plano", rotulo: "Plano", celula: (p: LinhaPedido) => p.checkout ? (p.checkout.sem_assinatura ? <>{p.checkout.nome}<span className="mtc-tabela__apoio" style={{ marginTop: "var(--e-2)" }}><SeloSemAssinatura /></span></> : p.checkout.nome) : <>{p.cobranca?.descricao ?? "Cobrança"}<span className="mtc-tabela__apoio">Mensalidade ou avanço</span></> },
    ...(profit ? [{ chave: "profit", rotulo: "Profit", celula: (p: LinhaPedido) => (p.plataforma ? PROFIT[p.plataforma] : null) }] : []),
    { chave: "valor", rotulo: "Valor", alinhar: "direita" as const, celula: (p: LinhaPedido) => <><span className="mtc-mono">{formatarReais(p.valor_centavos)}</span><span className="mtc-tabela__apoio">{MEIO[p.meio]}{p.parcelas > 1 ? ` em ${p.parcelas}x` : ""}{p.cupom ? `, cupom ${p.cupom} (- ${formatarReais(p.desconto_centavos)})` : ""}{p.promocao ? `, promoção ${p.promocao} (- ${formatarReais(p.desconto_centavos)})` : ""}</span></> },
    ...(status ? [{ chave: "status", rotulo: "Status", celula: (p: LinhaPedido) => <Selo tom={STATUS[p.status].tom}>{STATUS[p.status].rotulo}</Selo> }] : []),
    { chave: "pedido", rotulo: "Pedido", celula: (p: LinhaPedido) => <button type="button" className="mtc-link mtc-mono mtc-codigo-curto" title={p.codigo} onClick={() => aoAbrir(p.codigo)}>{p.codigo}</button> },
  ];
}

/**
 * Vendas pagas (Vitor, 15/10): abre em todo o periodo, como os Repasses; filtra por datas do pagamento, por valor e
 * pelo cliente (nome, CPF, e-mail ou pedido), e exporta em planilha com tudo o que o filtro acha. De 20 em 20 com o
 * total do filtro, contado no servico (spec 058, Fase 2): a lista nao corta mais em 500.
 */
function ListaDeVendas({ aoAbrir }: { aoAbrir: (c: string) => void }) {
  const { ler, arquivo } = useApi();
  const profit = temPlataformaProfit(useAmbiente().modulos);
  // Link com `?de=&ate=` abre com esse periodo (nasceu pro "Ver as vendas" da Visao Geral, que saiu em 20/10; o link vale).
  const [params] = useSearchParams();
  const dia = /^\d{4}-\d{2}-\d{2}$/;
  const daVisaoGeral = dia.test(params.get("de") ?? "") && dia.test(params.get("ate") ?? "");
  const [periodo, setPeriodo] = useState<"todo" | "datas">(daVisaoGeral ? "datas" : "todo");
  const [de, setDe] = useState(() => (daVisaoGeral ? params.get("de")! : `${hojeEmSaoPaulo().slice(0, 8)}01`));
  const [ate, setAte] = useState(() => (daVisaoGeral ? params.get("ate")! : hojeEmSaoPaulo()));
  const [minimo, setMinimo] = useState(0);
  const [maximo, setMaximo] = useState(0);
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");
  const [exportando, setExportando] = useState(false);
  const [erroExportar, setErroExportar] = useState<string>();
  useEffect(() => { const t = setTimeout(() => setTermo(busca.trim()), 300); return () => clearTimeout(t); }, [busca]);
  const comDatas = periodo === "datas";
  const filtro = new URLSearchParams({ ...(comDatas && de ? { de } : {}), ...(comDatas && ate ? { ate } : {}), ...(minimo ? { min_centavos: String(minimo) } : {}), ...(maximo ? { max_centavos: String(maximo) } : {}), ...(termo.length >= 2 ? { busca: termo } : {}) }).toString();
  const pag = usePagina("pagina", [filtro]);
  const vendas = useQuery({ queryKey: ["vendas", filtro, pag.pagina], queryFn: () => ler<{ itens: LinhaPedido[]; quantidade: number; total: number; pagina: number }>(PAG(`/v1/admin/vendas?pagina=${pag.pagina}&por_pagina=${POR_PAGINA}${filtro ? `&${filtro}` : ""}`)), placeholderData: (anterior) => anterior });
  const filtrando = comDatas || minimo > 0 || maximo > 0 || termo.length >= 2;
  const exportar = async () => {
    setErroExportar(undefined);
    setExportando(true);
    try {
      salvarArquivo(await arquivo(PAG(`/v1/admin/vendas/exportar${filtro ? `?${filtro}` : ""}`)), `vendas-${hojeEmSaoPaulo()}.csv`);
    } catch (e) {
      setErroExportar(erroDe(e));
    } finally {
      setExportando(false);
    }
  };
  const d = vendas.data;
  return (
    <Pilha espaco={3}>
      <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <Segmentado rotulo="Período" nome="periodo-das-vendas" valor={periodo} aoMudar={setPeriodo} opcoes={[{ valor: "todo", rotulo: "Todo o período" }, { valor: "datas", rotulo: "Escolher datas" }]} />
        {comDatas && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="De" valor={de} aoMudar={setDe} /></div>}
        {comDatas && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="Até" valor={ate} aoMudar={setAte} /></div>}
        <div style={{ flex: "0 1 160px" }}><CampoDinheiro rotulo="Valor de" centavos={minimo} aoMudar={setMinimo} /></div>
        <div style={{ flex: "0 1 160px" }}><CampoDinheiro rotulo="Valor até" centavos={maximo} aoMudar={setMaximo} /></div>
        <div style={{ flex: "1 1 220px" }}><Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, e-mail ou pedido" /></div>
        <Botao variante="secundario" carregando={exportando} disabled={!d || d.quantidade === 0} onClick={() => void exportar()}>Exportar planilha</Botao>
      </div>
      {erroExportar && <Aviso tipo="erro">{erroExportar}</Aviso>}
      {vendas.isLoading ? <Carregando compacto texto="Buscando as vendas" /> : vendas.isError ? <Aviso tipo="erro">{erroDe(vendas.error)}</Aviso> : (
        <>
          {/* Vitor, 20/10: sem "Vendas pagas" nem "Total pago" em cima da lista. O aviso de lista cortada saiu com as paginas. */}
          <Tabela linhas={d?.itens ?? []} paginacao={{ pagina: d?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: d?.total ?? 0 }} chave={(p) => p.codigo} aoClicar={(p) => aoAbrir(p.codigo)} vazio={filtrando ? <Vazio titulo="Nenhuma venda com esse filtro">Troque as datas, o valor ou a busca.</Vazio> : <Vazio titulo="Nenhuma venda ainda">As vendas pagas pelo checkout aparecem aqui.</Vazio>} colunas={colunasDoPedido(aoAbrir, { profit, status: false })} />
        </>
      )}
    </Pilha>
  );
}

function ListaDePedidos({ aoAbrir }: { aoAbrir: (c: string) => void }) {
  const { ler } = useApi();
  const colunas = colunasDoPedido(aoAbrir, { profit: false, status: true });
  const [status, setStatus] = useState<StatusPedido | "">("");
  const [busca, setBusca] = useState("");
  const q = new URLSearchParams({ ...(status ? { status } : {}), ...(busca.trim() ? { busca: busca.trim() } : {}) }).toString();
  const pag = usePagina("pagina", [q]);
  const pedidos = useQuery({ queryKey: ["pedidos", q, pag.pagina], queryFn: () => ler<{ itens: LinhaPedido[]; total: number; pagina: number }>(PAG(`/v1/admin/pedidos?pagina=${pag.pagina}&por_pagina=${POR_PAGINA}${q ? `&${q}` : ""}`)), placeholderData: (anterior) => anterior });
  return (
    <Pilha espaco={3}>
      <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <CampoSelecao rotulo="Status" valor={status} aoMudar={setStatus} vazio="Todos" opcoes={(Object.keys(STATUS) as StatusPedido[]).map((s) => ({ valor: s, rotulo: STATUS[s].rotulo }))} />
        <Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, e-mail ou pedido" />
      </div>
      {pedidos.isLoading ? <Carregando compacto texto="Buscando os pedidos" /> : pedidos.isError ? <Aviso tipo="erro">{erroDe(pedidos.error)}</Aviso> : (
        <Tabela linhas={pedidos.data?.itens ?? []} paginacao={{ pagina: pedidos.data?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: pedidos.data?.total ?? 0 }} chave={(p) => p.codigo} aoClicar={(p) => aoAbrir(p.codigo)} vazio={<Vazio titulo="Nenhum pedido">Nada com esse filtro.</Vazio>} colunas={[...colunas.slice(0, -1), { chave: "motivo", rotulo: "Motivo", celula: (p: LinhaPedido) => (p.motivo ? <Motivo titulo={p.motivo.titulo} detalhe={p.motivo.detalhe} /> : null) }, colunas.at(-1)!]} />
      )}
    </Pilha>
  );
}

/** Os avisos da Pagar.me de uma situacao, de 20 em 20 do servidor (spec 058, Fase 2). */
function useEventosDoGateway(status: "NAO_TRATADO" | "OUTRO_SISTEMA") {
  const { ler } = useApi();
  const pag = usePagina("pagina");
  const lista = useQuery({ queryKey: ["eventos-do-gateway", status, pag.pagina], queryFn: () => ler<{ itens: EventoDoGateway[]; total: number; pagina: number }>(PAG(`/v1/admin/eventos-do-gateway?status=${status}&pagina=${pag.pagina}&por_pagina=${POR_PAGINA}`)), placeholderData: (anterior) => anterior });
  return { lista, paginacao: { pagina: lista.data?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: lista.data?.total ?? 0 } };
}

function EventosNaoTratados() {
  const { lista, paginacao } = useEventosDoGateway("NAO_TRATADO");
  if (lista.isLoading) return <Carregando compacto texto="Buscando" />;
  if (lista.isError) return <Aviso tipo="erro">{erroDe(lista.error)}</Aviso>;
  return (
    <Pilha espaco={3}>
      <p className="mtc-texto-2">Avisos da Pagar.me que o sistema não soube tratar (tipo ou situação que ele não conhece, ou pedido que não é nosso). Nada aqui some sozinho: confira na Pagar.me.</p>
      <Tabela
        linhas={lista.data?.itens ?? []}
        paginacao={paginacao}
        chave={(e) => e.id}
        vazio={<Vazio titulo="Tudo reconhecido">Nenhum aviso pendente.</Vazio>}
        colunas={[
          { chave: "quando", rotulo: "Quando", celula: (e) => <span className="mtc-mono">{formatarDataHora(e.recebido_em)}</span> },
          { chave: "tipo", rotulo: "Tipo", celula: (e) => <span className="mtc-mono">{e.tipo}</span> },
          { chave: "pedido", rotulo: "Pedido", celula: (e) => <span className="mtc-mono">{e.pedido ?? ""}</span> },
          { chave: "erro", rotulo: "Por quê", celula: (e) => <span className="mtc-texto-2">{e.erro}</span> },
        ]}
      />
    </Pilha>
  );
}

/**
 * A conta da Pagar.me e a mesma dos dois sistemas ate a virada (spec 009, T4). O que o checkout novo nao criou aparece
 * aqui: guardado pra conferencia, sem virar pendencia de ninguem.
 */
function EventosDeOutroSistema() {
  // Aviso de pedido do sistema antigo na mesma conta da Pagar.me (spec 009, T4): so pra conferir, nunca pendencia.
  const { lista, paginacao } = useEventosDoGateway("OUTRO_SISTEMA");
  if (lista.isLoading) return <Carregando compacto texto="Buscando" />;
  if (lista.isError) return <Aviso tipo="erro">{erroDe(lista.error)}</Aviso>;
  return (
    <Pilha espaco={3}>
      <p className="mtc-texto-2">Pagamentos que chegaram na mesma conta da Pagar.me mas nasceram no sistema antigo. Ficam guardados só pra conferência: quem trata deles é o sistema antigo.</p>
      <Tabela
        linhas={lista.data?.itens ?? []}
        paginacao={paginacao}
        chave={(e) => e.id}
        vazio={<Vazio titulo="Nada do sistema antigo">Nenhum pagamento de fora chegou por aqui.</Vazio>}
        colunas={[
          { chave: "quando", rotulo: "Quando", celula: (e) => <span className="mtc-mono">{formatarDataHora(e.recebido_em)}</span> },
          { chave: "tipo", rotulo: "Tipo", celula: (e) => <span className="mtc-mono">{e.tipo}</span> },
          { chave: "erro", rotulo: "O que é", celula: (e) => <span className="mtc-texto-2">{e.erro}</span> },
        ]}
      />
    </Pilha>
  );
}

function DetalheDoPedido({ codigo, aoFechar }: { codigo: string | null; aoFechar: () => void }) {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const [erro, setErro] = useState<string>();
  const d = useQuery({ queryKey: ["pedido", codigo], queryFn: () => ler<DetalhePedido>(PAG(`/v1/admin/pedidos/${codigo}`)), enabled: Boolean(codigo) });
  const reenviar = async (id: string) => {
    setErro(undefined);
    try {
      await postar(PAG(`/v1/admin/eventos-de-saida/${id}/reenviar`));
      await qc.invalidateQueries({ queryKey: ["pedido", codigo] });
    } catch (err) {
      setErro(erroDe(err));
    }
  };
  const p = d.data;
  return (
    <Dialogo aberto={Boolean(codigo)} aoFechar={aoFechar} titulo={p ? `${p.comprador.nome}, ${formatarReais(p.valor_centavos)}` : "Pedido"} descricao={codigo ?? undefined}>
      {d.isLoading || !p ? <Carregando compacto texto="Buscando o pedido" /> : (
        <Pilha espaco={4}>
          <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap", alignItems: "center" }}>
            <Selo tom={STATUS[p.status].tom}>{STATUS[p.status].rotulo}</Selo>
            {p.checkout?.sem_assinatura && <SeloSemAssinatura />}
          </div>
          {p.motivo && <Aviso tipo={p.status === "RECUSADO" ? "erro" : "info"}><Motivo titulo={p.motivo.titulo} detalhe={p.motivo.detalhe} /></Aviso>}
          <Fatos itens={[
            { rotulo: p.checkout?.sem_assinatura ? "Referente a" : "Plano", valor: p.itens.map((i) => `${i.nome}${i.plataforma ? ` (${PLATAFORMA[i.plataforma as keyof typeof PLATAFORMA] ?? i.plataforma})` : ""}`).join(", ") },
            { rotulo: "Comprador", valor: `${p.comprador.cpf}, ${p.comprador.email}, ${p.telefone}` },
            { rotulo: "Meio", valor: `${MEIO[p.meio]}${p.parcelas > 1 ? ` em ${p.parcelas}x` : ""}` },
            // O que deu o desconto (cupom, spec 016, ou promocao, spec 067; nunca os dois), com quanto tirou.
            ...(p.cupom ? [{ rotulo: "Cupom", valor: `${p.cupom} (- ${formatarReais(p.desconto_centavos)})` }] : []),
            ...(p.promocao ? [{ rotulo: "Promoção", valor: `${p.promocao} (- ${formatarReais(p.desconto_centavos)})` }] : []),
            ...(p.cartao ? [{ rotulo: "Cartão", valor: `${p.cartao.bandeira ?? ""} final ${p.cartao.final ?? "?"}${p.cartao.titular ? `, titular ${p.cartao.titular}` : ""}${p.cartao.titular_documento ? ` (${p.cartao.titular_documento})` : ""}` }] : []),
            ...(p.pix?.e2e ? [{ rotulo: "PIX E2E", valor: p.pix.e2e, mono: true }] : []),
            { rotulo: "Criado", valor: formatarDataHora(p.criado_em) },
            ...(p.pago_em ? [{ rotulo: "Pago", valor: formatarDataHora(p.pago_em) }] : []),
            // Cobranca de mensalidade nao tem checkout nem aceite: a linha so aparece em compra pelo checkout.
            ...(p.checkout ? [linhaDoAceite(p.aceite)] : []),
          ]} />
          <span className="mtc-rotulo">{p.checkout?.sem_assinatura ? "Avisos pro sistema (e-mail, sem matrícula)" : "Avisos pro sistema (matrícula e e-mail)"}</span>
          {p.eventos_de_saida.length === 0 ? <span className="mtc-texto-2">Nenhum ainda.</span> : p.eventos_de_saida.map((e) => (
            <div key={e.id} className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap", alignItems: "center" }}>
              <span className="mtc-mono">{e.tipo}</span>
              <Selo tom={e.entregue_em ? "sucesso" : "alerta"}>{e.entregue_em ? "entregue" : `pendente (${e.tentativas} tentativas)`}</Selo>
              {e.ultimo_erro && <span className="mtc-texto-2">{e.ultimo_erro}</span>}
              {pode("ADMIN") && <Botao variante="fantasma" tamanho="pequeno" onClick={() => void reenviar(e.id)}>Reenviar</Botao>}
            </div>
          ))}
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
        </Pilha>
      )}
    </Dialogo>
  );
}

/**
 * CPFs bloqueados pra compras novas (regulamento geral: chargeback bloqueia o CPF). So o admin bloqueia e desbloqueia
 * (Vitor, 14/09). O checkout recusa sem dizer o motivo; os planos em andamento seguem ate alguem decidir.
 */
function CpfsBloqueados() {
  const pagCpfs = usePagina("pagina_cpfs");
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  // Decisao sobre pessoa: mora no backend (spec 000, regra 3); o checkout pergunta la antes de vender.
  const q = useQuery({ queryKey: ["cpfs-bloqueados"], queryFn: () => ler<{ itens: CpfBloqueado[] }>("/v1/admin/cpfs-bloqueados") });
  const [novo, setNovo] = useState(false);
  const [desbloquear, setDesbloquear] = useState<CpfBloqueado | null>(null);
  const [cpf, setCpf] = useState("");
  const [motivo, setMotivo] = useState("");
  const [feito, setFeito] = useState<string>();
  const recarregar = () => qc.invalidateQueries({ queryKey: ["cpfs-bloqueados"] });
  return (
    <Pilha espaco={3}>
      <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-3)" }}>
        <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>O checkout recusa compra nova destes CPFs, sem dizer o motivo. Os planos em andamento seguem até alguém decidir.</p>
        {pode("ADMIN") && <Botao variante="secundario" tamanho="pequeno" onClick={() => setNovo(true)}>Bloquear CPF</Botao>}
      </div>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          paginacao={pagCpfs}
          linhas={q.data?.itens ?? []}
          chave={(c) => c.cpf}
          vazio={<Vazio titulo="Nenhum CPF bloqueado">Quando o admin bloquear um CPF (ex.: chargeback), ele aparece aqui.</Vazio>}
          colunas={[
            { chave: "cpf", rotulo: "CPF", celula: (c) => <span className="mtc-mono">{c.cpf}</span> },
            { chave: "motivo", rotulo: "Motivo", celula: (c) => c.motivo },
            { chave: "quando", rotulo: "Bloqueado em", celula: (c) => <><span className="mtc-mono">{formatarDataHora(c.bloqueado_em)}</span>{c.desbloqueado_em && <span className="mtc-tabela__apoio">Desbloqueado em {formatarDataHora(c.desbloqueado_em)}</span>}</> },
            { chave: "status", rotulo: "Situação", celula: (c) => <Selo tom={c.ativo ? "erro" : "neutro"}>{c.ativo ? "Bloqueado" : "Desbloqueado"}</Selo> },
            { chave: "acao", rotulo: "", celula: (c) => (c.ativo && pode("ADMIN") ? <Botao variante="fantasma" tamanho="pequeno" onClick={() => setDesbloquear(c)}>Desbloquear</Botao> : null) },
          ]}
        />
      )}
      <DialogoDeAcao aberto={novo} aoFechar={() => setNovo(false)} titulo="Bloquear CPF" descricao="O checkout passa a recusar compra nova deste CPF. Não mexe nos planos que a pessoa já tem." botao="Bloquear" variante="perigo" podeEnviar={cpf.replace(/\D/g, "").length === 11 && motivo.trim().length >= 5} aoAbrir={() => { setCpf(""); setMotivo(""); }} aoEnviar={() => postar("/v1/admin/cpfs-bloqueados", { cpf, motivo: motivo.trim() })} aoConcluir={() => { void recarregar(); setFeito("CPF bloqueado pra compras novas."); }}>
        <CampoCpf valor={cpf} aoMudar={setCpf} />
        <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: chargeback do pedido mtc_... em 14/09." dica="Fica no registro; o comprador não vê." />
      </DialogoDeAcao>
      <DialogoDeAcao aberto={Boolean(desbloquear)} aoFechar={() => setDesbloquear(null)} titulo="Desbloquear CPF" descricao={desbloquear ? `${desbloquear.cpf} volta a poder comprar pelo checkout.` : undefined} botao="Desbloquear" aoEnviar={() => postar(`/v1/admin/cpfs-bloqueados/${desbloquear!.cpf.replace(/\D/g, "")}/desbloquear`)} aoConcluir={() => { void recarregar(); setFeito("CPF desbloqueado."); }} />
    </Pilha>
  );
}
